import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	ShortfallExcessConformationModalComponent,
	ShortfallExcessConformationModalData,
} from '@components/documents-matter-viewer/shortfall-excess-conformation-modal/shortfall-excess-conformation-modal.component';
import { fade } from '@core/animations/animations';
import { createId } from '@core/util/file';
import { ICON } from '@core/util/icon';
import { centsToDollars } from '@core/util/money';
import { BusinessSettings } from '@models/businessSettings/settings';
import {
	LedgerType,
	CustomTrustLedgerItem,
	TrustLedgerItemType,
	DefaultCustomLedgerItem,
} from '@models/trustLedger';
import { MatterFileType } from '@models/matter';
import {
	MatterMortgagePriority,
	MatterMortgageWithAssociations,
} from '@models/mortgage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { BusinessState } from '@state/business';
import { TagsSelector } from '@state/global/tags/tags.selector';
import { TrustLedgerSelectors } from '@state/global/trust-ledger/trust_ledger';
import {
	LedgerItemType,
	TrustLedger,
	TrustLedgerItem,
	TrustLedgerTotals,
} from '@state/global/trust-ledger/trust_ledger.models';
import { MatterDispatch } from '@state/matter';
import { MatterWordDocumentsDefaultsSource } from '@state/matter/matter-word-documents/matter-word-documents-defaults-source.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import {
	MatterMortgageDispatch,
	MatterMortgagesState,
} from '@state/matter/matter_mortgages';
import { MatterStatementofAccountSelectors } from '@state/matter/statementOfAccounts/statementOfAccounts.selectors';
import { cloneDeep } from 'lodash';
import { combineLatest, Observable, take } from 'rxjs';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { StatementOfAccountTotals } from 'src/app/shared/services/financialTotals/financial-totals.service';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { PILL_TYPES } from 'src/app/ui/pill/pill.component';
import { SplitViewService } from 'src/app/ui/split-view/split-view.service';
import { UrlService } from '../../../shared/services/url/url.service';
import { GlobalProfileComponent } from '../../global-profile/global-profile.component';
import { GlobalProfileNavType } from '../../global-profile/global-profile.types';
import { DebtData } from '../debts';
import { DebtDialogComponent } from '../debts/debt-dialog';
import {
	EditGeneratedTitleComponent,
	EditGeneratedTitleModalData,
} from './edit-generated-title/edit-generated-title.component';
import { LedgerInstructionsModalComponent } from './ledger-instructions-modal/ledger-instructions-modal.component';
import { TestHooks } from './ledger.hooks';
import { MortgageDrawerComponent } from './mortgage-drawer/mortgage-drawer.component';
import {
	PaymentReceiptModalComponent,
	PaymentReceiptModalData,
} from './payment-receipt-modal/payment-receipt-modal.component';
import { SalePriceItemDrawerComponent } from './sale-price-item-drawer/sale-price-item-drawer.component';
import { SummaryDocketComponent } from '../teranet/summary-docket/summary-docket.component';

enum PaymentLineOption {
	BILL = 'bill',
	CUSTOM = 'custom',
}

const AddPaymentLineOption: DropdownOption<PaymentLineOption>[] = [
	{
		name: 'Bill or Debt',
		value: PaymentLineOption.BILL,
	},
	{
		name: 'Custom',
		value: PaymentLineOption.CUSTOM,
	},
];

@UntilDestroy()
@Component({
	selector: 'app-ledger',
	templateUrl: './ledger.component.html',
	animations: [fade()],
	styles: [
		`
			.cdk-drag-preview {
				@apply bg-white rounded shadow-lg opacity-80;
			}

			.cdk-drag-placeholder {
				@apply opacity-0;
			}

			.cdk-drag-animating {
				@apply transition-transform duration-300 ease-out;
			}

			.cdk-drop-list-dragging .cdk-drag:not(.cdk-drag-placeholder) {
				@apply transition-transform duration-300 ease-out;
			}
		`,
	],
	standalone: false,
})
export class LedgerComponent implements OnInit {
	private readonly deletableTypes = [
		LedgerItemType.TERAVIEW_FEE,
		LedgerItemType.CUSTOM,
	];
	testHooks = TestHooks;
	accountTotal: number;
	backUrlParams = { 'back-string': 'trust ledger' };
	documentId: string;
	public ICON = ICON;
	public PILL_TYPES = PILL_TYPES;
	trustLedgerTotals: TrustLedgerTotals;
	trustLedgerReceiptItems: TrustLedgerItem[];
	trustLedgerPaymentItems: TrustLedgerItem[];
	trustLedgerReceiptItemDescriptions: string[] = [];
	trustLedgerPaymentItemDescriptions: string[] = [];
	paymentLineOptions = AddPaymentLineOption;
	showPaymentOptions = false;
	math = Math;
	ledgerItemTypes = LedgerItemType;
	customLedgerItem = LedgerItemType.CUSTOM;
	mortgageItem = LedgerItemType.MORTGAGE;
	salePriceItem = LedgerItemType.SALE_PRICE;

	splitViewService = inject(SplitViewService);

	@Select(SelectedMatter.useHtmlDocuments)
	public useHtmlDocuments$: Observable<boolean>;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@ViewSelectSnapshot(
		MatterStatementofAccountSelectors.statementOfAccountsTotals,
	)
	statementOfAccountTotals: StatementOfAccountTotals;

	@Select(TrustLedgerSelectors.getTrustLedger)
	trustLedger$: Observable<TrustLedger>;
	@Select(TagsSelector.getTagInjectedDocument)
	getTagInjectedDocument$: Observable<(doc: string) => string>;
	@ViewSelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@SelectSnapshot(BusinessState.getDefaultSettings) settings: BusinessSettings;

	disabledElements: string[];

	@Select(MatterMortgagesState.getMortgageWithAssociations)
	mortgageFilterFn$: Observable<(id: string) => MatterMortgageWithAssociations>;

	@Select(BusinessState.getShowWordSideBySide)
	showWordSideBySide$: Observable<boolean>;
	@Select(MatterWordDocumentsDefaultsSource.getTrustLedgerSourceIdByMatterType)
	statementOfTrustLedgerDocSourceIdByMatterType$: Observable<string>;

	constructor(
		private dialog: MatDialog,
		private matterDispatch: MatterDispatch,
		private matterMortgageDispatch: MatterMortgageDispatch,
		private navService: NavigationService,
		private drawerService: DrawerService,
		private analytics: AnalyticsService,
	) {}

	ngOnInit(): void {
		combineLatest([this.trustLedger$, this.getTagInjectedDocument$])
			.pipe(untilDestroyed(this))
			.subscribe(([trustLedger, getTagInjectedDocument]) => {
				this.trustLedgerReceiptItems = trustLedger?.items?.filter(
					(x) => x.ledgerType === LedgerType.RECEIPT,
				);
				this.trustLedgerPaymentItems = trustLedger?.items
					?.filter((x) => x.ledgerType === LedgerType.PAYMENT)
					.map((x) => {
						const isDeletableType =
							x.type && this.deletableTypes.includes(x.type);

						if (isDeletableType && x.sourceId !== 'combined-transfer') {
							return {
								...x,
								deletable: true,
							};
						}
						return x;
					});
				this.trustLedgerTotals = trustLedger?.totals;
				this.trustLedgerPaymentItemDescriptions = [];
				this.trustLedgerReceiptItemDescriptions = [];
				this.trustLedgerReceiptItems.forEach((x) => {
					this.trustLedgerReceiptItemDescriptions.push(
						getTagInjectedDocument(x.description),
					);
				});
				this.trustLedgerPaymentItems.forEach((x) => {
					this.trustLedgerPaymentItemDescriptions.push(
						getTagInjectedDocument(x.description),
					);
				});
				if (trustLedger.totals?.difference !== 0) {
					this.disabledElements = ['menuButton'];
				} else {
					this.disabledElements = [];
				}
			});
	}

	addDebt(): void {
		this.openDebtModal();
	}

	addPaymentFromClient() {
		const data: PaymentReceiptModalData = {
			item: {
				...DefaultCustomLedgerItem,
				description: this.settings.trustLedger.defaultDescriptionCashFall,
				amount: Math.abs(this.trustLedgerTotals.difference),
				type: TrustLedgerItemType.TO_FROM_CLIENT,
			},
		};
		const dialog = this.dialog.open(PaymentReceiptModalComponent, { data });
		dialog
			.afterClosed()
			.pipe(take(1))
			.subscribe((result) => {
				if (result) {
					const newId = createId();
					this.matterDispatch.UpdateReceipt(this.matterId, newId, result);
				}
			});
	}

	addPaymentToClient() {
		const data: PaymentReceiptModalData = {
			item: {
				...DefaultCustomLedgerItem,
				description: 'Paid to you following closing',
				amount: Math.abs(this.trustLedgerTotals.difference),
				type: TrustLedgerItemType.TO_FROM_CLIENT,
			},
		};
		const dialog = this.dialog.open(PaymentReceiptModalComponent, { data });
		dialog
			.afterClosed()
			.pipe(take(1))
			.subscribe((result) => {
				if (result) {
					const newId = createId();
					this.matterDispatch.UpdatePayment(this.matterId, newId, result);
				}
			});
	}

	addReceipt(): void {
		const data: PaymentReceiptModalData = {
			item: {
				...DefaultCustomLedgerItem,
			},
		};
		const dialog = this.dialog.open(PaymentReceiptModalComponent, { data });
		dialog
			.afterClosed()
			.pipe(take(1))
			.subscribe((result) => {
				if (result) {
					const newId = createId();
					this.matterDispatch.UpdateReceipt(this.matterId, newId, result);
				}
			});
	}

	editCustomReceipt(id: string, item: CustomTrustLedgerItem) {
		const data: PaymentReceiptModalData = { item };
		const dialog = this.dialog.open(PaymentReceiptModalComponent, { data });
		dialog
			.afterClosed()
			.pipe(take(1))
			.subscribe((result) => {
				if (result) {
					this.matterDispatch.UpdateReceipt(this.matterId, id, result);
				}
			});
	}

	editReceiptTitle(item: TrustLedgerItem) {
		const data: EditGeneratedTitleModalData = {
			description: item.description,
		};

		const dialog = this.dialog.open(EditGeneratedTitleComponent, { data });
		dialog
			.afterClosed()
			.pipe(take(1))
			.subscribe((description) => {
				if (description && description?.length > 0) {
					this.matterDispatch.SetReceiptDescriptionOverride(
						this.matterId,
						item.id,
						description,
					);
				}
			});
	}

	addPayment(): void {
		const data: PaymentReceiptModalData = {
			item: {
				...DefaultCustomLedgerItem,
			},
		};
		const dialog = this.dialog.open(PaymentReceiptModalComponent, { data });
		dialog
			.afterClosed()
			.pipe(take(1))
			.subscribe((result) => {
				if (result) {
					const newId = createId();
					this.matterDispatch.UpdatePayment(this.matterId, newId, result);
				}
			});
	}

	editCustomPayment(id: string, item: CustomTrustLedgerItem) {
		const data: PaymentReceiptModalData = { item };
		const dialog = this.dialog.open(PaymentReceiptModalComponent, { data });
		dialog
			.afterClosed()
			.pipe(take(1))
			.subscribe((result) => {
				if (result) {
					this.matterDispatch.UpdatePayment(this.matterId, id, result);
				}
			});
	}

	editPaymentTitle(item: TrustLedgerItem) {
		const data: EditGeneratedTitleModalData = {
			description: item.description,
		};

		const dialog = this.dialog.open(EditGeneratedTitleComponent, { data });
		dialog
			.afterClosed()
			.pipe(take(1))
			.subscribe((description) => {
				if (description && description?.length > 0) {
					this.matterDispatch.SetPaymentDescriptionOverride(
						this.matterId,
						item.id,
						description,
					);
				}
			});
	}

	sortReceipts(event: CdkDragDrop<string[]>) {
		const reOrderedReceipts = cloneDeep(this.trustLedgerReceiptItems);
		moveItemInArray(reOrderedReceipts, event.previousIndex, event.currentIndex);
		const orderedReceiptIds = reOrderedReceipts?.map((x) => x.id);
		this.matterDispatch.SortReceipts(this.matterId, orderedReceiptIds);
	}

	sortPayments(event: CdkDragDrop<string[]>) {
		const reOrderedPayments = cloneDeep(this.trustLedgerPaymentItems);
		moveItemInArray(reOrderedPayments, event.previousIndex, event.currentIndex);
		const orderedReceiptIds = reOrderedPayments?.map((x) => x.id);
		this.matterDispatch.SortPayments(this.matterId, orderedReceiptIds);
	}

	get showExcessAmount() {
		return Number(centsToDollars(this.statementOfAccountTotals?.owing)) < 0;
	}

	get showBalanceOwing() {
		return Number(centsToDollars(this.statementOfAccountTotals?.owing)) > 0;
	}

	selectAddPaymentLine(
		$event: DropdownResult<DropdownOption<PaymentLineOption>>,
	) {
		if ($event.result.value === PaymentLineOption.BILL) {
			this.addDebt();
		} else if ($event.result.value === PaymentLineOption.CUSTOM) {
			this.addPayment();
		}
		this.showPaymentOptions = false;
	}

	openDebtModal(debtId = ''): void {
		this.dialog.open(DebtDialogComponent, {
			autoFocus: true,
			data: {
				debtId,
				matterId: this.matterId,
			} as DebtData,
		});
	}

	openDocketSummaryModal(): void {
		this.dialog.open(SummaryDocketComponent, { width: '70vw', height: '80vh' });
	}

	confirmationAction = (action: () => void): void => {
		if (this.trustLedgerTotals?.difference !== 0) {
			const dialogRef = this.dialog.open(
				ShortfallExcessConformationModalComponent,
				{
					data: {
						context:
							this.trustLedgerTotals?.difference < 0 ? 'shortfall' : 'excess',
						amount: this.trustLedgerTotals?.difference,
					} as ShortfallExcessConformationModalData,
					width: '100%',
					maxWidth: '550px',
				},
			);

			dialogRef
				.afterClosed()
				.pipe(take(1))
				.subscribe((result) => {
					if (result) {
						action();
					}
				});
		} else {
			action();
		}
	};

	removeReceipt(id: string): void {
		this.matterDispatch.DeleteReceipt(this.matterId, id);
	}

	removePayment(trustLedgerItem: TrustLedgerItem): void {
		if (trustLedgerItem?.type === LedgerItemType.TERAVIEW_FEE) {
			this.matterMortgageDispatch.updateMortgage(trustLedgerItem.sourceId, {
				hideRegistrationFee: true,
			});
		}
		this.matterDispatch.DeletePayment(this.matterId, trustLedgerItem.id);
	}

	getQueryParams(trustLedgerItem: TrustLedgerItem) {
		const queryparams = { 'back-string': 'trust ledger' };

		if (trustLedgerItem.type === LedgerItemType.LEDGER_INSTRUCTIONS) {
			queryparams['modal'] = 'instructions';
		}

		return queryparams;
	}

	goToItem(trustLedgerItem: TrustLedgerItem) {
		if (trustLedgerItem.type === LedgerItemType.TITLE_SEARCH_FEE) {
			this.openDocketSummaryModal();
			return;
		}

		if (trustLedgerItem.type === LedgerItemType.LEDGER_INSTRUCTIONS) {
			this.dialog.open(LedgerInstructionsModalComponent, {
				disableClose: true,
			});
		} else {
			const url = this.getAnchor(trustLedgerItem);
			if (url) this.navService.goToUrl(url, { ...this.backUrlParams });
		}
	}

	editMortgage(trustLedgerItem: TrustLedgerItem) {
		this.analytics.logEvent(AnalyticsEvent.MORTGAGE_DRAWER_OPEN_BUTTON_CLICK);
		const mortgageId = trustLedgerItem.id;
		let mortgage: MatterMortgageWithAssociations;
		let drawerTitle: string;
		this.mortgageFilterFn$.pipe(untilDestroyed(this)).subscribe((fn) => {
			mortgage = fn(mortgageId);
		});
		switch (mortgage?.priority) {
			case MatterMortgagePriority.PRIORITY_1:
				drawerTitle = 'Edit First Mortgage Details';
				break;
			case MatterMortgagePriority.PRIORITY_2:
				drawerTitle = 'Edit Second Mortgage Details';
				break;
			case MatterMortgagePriority.PRIORITY_3:
				drawerTitle = 'Edit Third Mortgage Details';
				break;
			case MatterMortgagePriority.PRIORITY_4:
				drawerTitle = 'Edit Fourth Mortgage Details';
				break;
			case MatterMortgagePriority.PRIORITY_5:
				drawerTitle = 'Edit Fifth Mortgage Details';
				break;
			default:
				drawerTitle = 'Edit Mortgage Details';
				break;
		}
		this.drawerService.openDrawer(MortgageDrawerComponent, {
			data: {
				mortgageId: mortgageId,
				matterId: this.matterId,
				trustLedgerItem: trustLedgerItem,
			},
			title: drawerTitle,
			size: 'sm',
		});
	}

	editSalePriceItem(trustLedgerItem: TrustLedgerItem) {
		const drawerTitle = 'Edit item';
		this.drawerService.openDrawer(SalePriceItemDrawerComponent, {
			data: {
				matterId: this.matterId,
				trustLedgerItem: trustLedgerItem,
			},
			title: drawerTitle,
			size: 'sm',
		});
	}

	getAnchor(trustLedgerItem: TrustLedgerItem) {
		switch (trustLedgerItem.type) {
			case LedgerItemType.BILL_DEBTS:
				return UrlService.Matter_Debts(this.matterId);
			case LedgerItemType.BUYER_BROKERAGE_COMMISSION:
				return UrlService.Matter_Commission_Buyer_Brokerage(this.matterId);
			case LedgerItemType.SELLER_BROKERAGE_COMMISSION:
				return UrlService.Matter_Commission_Seller_Brokerage(this.matterId);
			case LedgerItemType.DEPOSIT:
				return UrlService.Matter_Adjustments(this.matterId);
			case LedgerItemType.ENCUMBRANCE:
				return UrlService.Matter_Encumbrances_Encumbrance(
					this.matterId,
					trustLedgerItem?.sourceId,
				);
			case LedgerItemType.IDV_FEE:
				this.drawerService.openDrawer(GlobalProfileComponent, {
					data: {
						clientId: trustLedgerItem?.sourceId,
						tab: GlobalProfileNavType.IDENTIFICATION,
					},
				});
				return '';
			case LedgerItemType.FEES_DISBURSEMENTS:
				return UrlService.Matter_Accounts(this.matterId);
			case LedgerItemType.LTT:
				return UrlService.Matter_Ltt(this.matterId);
			case LedgerItemType.MORTGAGE:
				return UrlService.Matter_Mortgages_Mortgage(
					this.matterId,
					trustLedgerItem?.sourceId,
				);
			case LedgerItemType.TERAVIEW_FEE:
				return UrlService.Matter_Mortgages_Mortgage(
					this.matterId,
					trustLedgerItem?.sourceId,
				);
			case LedgerItemType.SALE_PRICE:
				return UrlService.Matter_Adjustments(this.matterId);
			case LedgerItemType.LEDGER_INSTRUCTIONS:
				return UrlService.Matter_Ledger(this.matterId);
			case LedgerItemType.TITLE_INSURANCE:
				return UrlService.Matter_TitleInsurance(this.matterId);
			case LedgerItemType.MORTGAGE_BROKER_FEE:
				return UrlService.Matter_Mortgages_Mortgage(
					this.matterId,
					trustLedgerItem?.sourceId,
				);
		}

		return '';
	}

	getAnchorText(trustLedgerItem: TrustLedgerItem) {
		switch (trustLedgerItem.type) {
			case LedgerItemType.BILL_DEBTS:
				return 'bill or debt';
			case LedgerItemType.BUYER_BROKERAGE_COMMISSION:
				return 'buyer brokerage commission';
			case LedgerItemType.SELLER_BROKERAGE_COMMISSION:
				return 'seller brokerage commission';
			case LedgerItemType.DEPOSIT:
				return 'adjustments';
			case LedgerItemType.ENCUMBRANCE:
				return 'encumbrances';
			case LedgerItemType.FEES_DISBURSEMENTS:
				return 'disbursement';
			case LedgerItemType.MORTGAGE:
				return 'mortgage';
			case LedgerItemType.SALE_PRICE:
				return 'sale price';
			case LedgerItemType.LEDGER_INSTRUCTIONS:
				return 'closing instructions';
			case LedgerItemType.TITLE_SEARCH_FEE:
				return 'docket summary';
		}

		return '';
	}

	ledgerItemId(_, item: TrustLedgerItem): string {
		return item.id;
	}

	trackByKey(index: number, item: { key: string; value: string }): string {
		return item.key;
	}
}
